/**
 * Contract Formatter Utility Functions
 * US-CONT-005: View Expiring Contracts Alert
 * Formats dates, currency, and other display values
 */

import { format, parseISO, formatDistanceToNow } from 'date-fns';
import type { ContractType } from '../types/expiringContracts.types';
import { CONTRACT_TYPE_LABELS, DATE_FORMATS } from '../constants/expiringContracts.constants';

/**
 * Format expiry date for display
 * @param endDate - Contract end date (ISO 8601 string)
 * @param formatString - Optional format string (default: MMM dd, yyyy)
 * @returns Formatted date string
 */
export function formatExpiryDate(endDate: string, formatString: string = DATE_FORMATS.DISPLAY): string {
  try {
    const date = parseISO(endDate);
    return format(date, formatString);
  } catch (error) {
    console.error('Error formatting expiry date:', error);
    return endDate;
  }
}

/**
 * Format start date for display
 * @param startDate - Contract start date (ISO 8601 string)
 * @param formatString - Optional format string (default: MMM dd, yyyy)
 * @returns Formatted date string
 */
export function formatStartDate(startDate: string, formatString: string = DATE_FORMATS.DISPLAY): string {
  try {
    const date = parseISO(startDate);
    return format(date, formatString);
  } catch (error) {
    console.error('Error formatting start date:', error);
    return startDate;
  }
}

/**
 * Format days remaining into human-readable string
 * @param daysRemaining - Number of days until expiry
 * @returns Formatted string (e.g., "5 days", "1 day", "Today", "Expired")
 */
export function formatDaysRemaining(daysRemaining: number): string {
  if (daysRemaining < 0) {
    const daysAgo = Math.abs(daysRemaining);
    return `Expired ${daysAgo} ${daysAgo === 1 ? 'day' : 'days'} ago`;
  }

  if (daysRemaining === 0) {
    return 'Expires today';
  }

  if (daysRemaining === 1) {
    return 'Expires tomorrow';
  }

  return `${daysRemaining} days remaining`;
}

/**
 * Format days remaining as short badge text
 * @param daysRemaining - Number of days until expiry
 * @returns Short formatted string (e.g., "5d", "Today", "Expired")
 */
export function formatDaysRemainingShort(daysRemaining: number): string {
  if (daysRemaining < 0) {
    return 'Expired';
  }

  if (daysRemaining === 0) {
    return 'Today';
  }

  if (daysRemaining === 1) {
    return 'Tomorrow';
  }

  return `${daysRemaining}d`;
}

/**
 * Format contract value as currency
 * @param value - Contract value number
 * @param currency - Currency code (default: INR)
 * @returns Formatted currency string
 */
export function formatContractValue(value: number | undefined, currency: string = 'INR'): string {
  if (value === undefined || value === null) {
    return 'N/A';
  }

  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  } catch (error) {
    console.error('Error formatting contract value:', error);
    return `${currency} ${value.toLocaleString()}`;
  }
}

/**
 * Format contract value in compact format (K, L, Cr)
 * @param value - Contract value number
 * @param currency - Currency code (default: INR)
 * @returns Compact formatted string (e.g., "₹5.5L", "₹2.3Cr")
 */
export function formatContractValueCompact(value: number | undefined, currency: string = 'INR'): string {
  if (value === undefined || value === null) {
    return 'N/A';
  }

  const currencySymbol = currency === 'INR' ? '₹' : currency;

  // Crores (10,000,000)
  if (value >= 10000000) {
    return `${currencySymbol}${(value / 10000000).toFixed(1)}Cr`;
  }

  // Lakhs (100,000)
  if (value >= 100000) {
    return `${currencySymbol}${(value / 100000).toFixed(1)}L`;
  }

  // Thousands
  if (value >= 1000) {
    return `${currencySymbol}${(value / 1000).toFixed(1)}K`;
  }

  return `${currencySymbol}${value}`;
}

/**
 * Get display label for contract type
 * @param contractType - Contract type enum value
 * @returns Human-readable label
 */
export function getContractTypeLabel(contractType: ContractType): string {
  return CONTRACT_TYPE_LABELS[contractType] || contractType;
}

/**
 * Format contract number for display
 * @param contractNumber - Contract number string
 * @returns Formatted contract number
 */
export function formatContractNumber(contractNumber: string): string {
  if (!contractNumber) {
    return 'N/A';
  }

  // If contract number is too long, truncate with ellipsis
  if (contractNumber.length > 20) {
    return `${contractNumber.substring(0, 17)}...`;
  }

  return contractNumber;
}

/**
 * Format customer name for display
 * @param customerName - Customer name string
 * @param maxLength - Maximum length before truncation
 * @returns Formatted customer name
 */
export function formatCustomerName(customerName: string, maxLength: number = 30): string {
  if (!customerName) {
    return 'Unknown';
  }

  if (customerName.length > maxLength) {
    return `${customerName.substring(0, maxLength - 3)}...`;
  }

  return customerName;
}

/**
 * Format phone number for display
 * @param phone - Phone number string
 * @returns Formatted phone number
 */
export function formatPhoneNumber(phone: string | undefined): string {
  if (!phone) {
    return 'N/A';
  }

  // Remove non-digit characters
  const cleaned = phone.replace(/\D/g, '');

  // Format Indian phone numbers: +91 XXXXX XXXXX
  if (cleaned.length === 10) {
    return `+91 ${cleaned.substring(0, 5)} ${cleaned.substring(5)}`;
  }

  if (cleaned.length === 12 && cleaned.startsWith('91')) {
    return `+91 ${cleaned.substring(2, 7)} ${cleaned.substring(7)}`;
  }

  return phone;
}

/**
 * Format email for display (truncate if too long)
 * @param email - Email string
 * @param maxLength - Maximum length before truncation
 * @returns Formatted email
 */
export function formatEmail(email: string | undefined, maxLength: number = 30): string {
  if (!email) {
    return 'N/A';
  }

  if (email.length > maxLength) {
    const [localPart, domain] = email.split('@');
    if (localPart && domain) {
      return `${localPart.substring(0, 10)}...@${domain}`;
    }
  }

  return email;
}

/**
 * Format timestamp to relative time
 * @param timestamp - ISO 8601 timestamp string
 * @returns Relative time string (e.g., "2 hours ago")
 */
export function formatRelativeTime(timestamp: string): string {
  try {
    const date = parseISO(timestamp);
    return formatDistanceToNow(date, { addSuffix: true });
  } catch (error) {
    console.error('Error formatting relative time:', error);
    return timestamp;
  }
}

/**
 * Format date range for display
 * @param startDate - Start date (ISO 8601 string)
 * @param endDate - End date (ISO 8601 string)
 * @returns Formatted date range string
 */
export function formatDateRange(startDate: string, endDate: string): string {
  try {
    const start = format(parseISO(startDate), DATE_FORMATS.DISPLAY);
    const end = format(parseISO(endDate), DATE_FORMATS.DISPLAY);
    return `${start} - ${end}`;
  } catch (error) {
    console.error('Error formatting date range:', error);
    return `${startDate} - ${endDate}`;
  }
}

/**
 * Format duration in days
 * @param startDate - Start date (ISO 8601 string)
 * @param endDate - End date (ISO 8601 string)
 * @returns Duration string (e.g., "365 days", "1 year")
 */
export function formatContractDuration(startDate: string, endDate: string): string {
  try {
    const start = parseISO(startDate);
    const end = parseISO(endDate);
    const days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

    if (days < 30) {
      return `${days} ${days === 1 ? 'day' : 'days'}`;
    }

    if (days < 365) {
      const months = Math.floor(days / 30);
      return `${months} ${months === 1 ? 'month' : 'months'}`;
    }

    const years = Math.floor(days / 365);
    return `${years} ${years === 1 ? 'year' : 'years'}`;
  } catch (error) {
    console.error('Error formatting contract duration:', error);
    return 'N/A';
  }
}

/**
 * Format percentage value
 * @param value - Percentage value (0-100)
 * @param decimals - Number of decimal places
 * @returns Formatted percentage string
 */
export function formatPercentage(value: number, decimals: number = 1): string {
  return `${value.toFixed(decimals)}%`;
}

/**
 * Sanitize search query
 * @param query - Search query string
 * @returns Sanitized query
 */
export function sanitizeSearchQuery(query: string): string {
  return query.trim().toLowerCase();
}

/**
 * Truncate text with ellipsis
 * @param text - Text to truncate
 * @param maxLength - Maximum length
 * @returns Truncated text
 */
export function truncateText(text: string, maxLength: number): string {
  if (!text || text.length <= maxLength) {
    return text;
  }

  return `${text.substring(0, maxLength - 3)}...`;
}
